import { useContext, useEffect, useRef } from 'react';
import { UserDetailsContext } from '../context/user-details/user-details';
import UserDetailsService from '../shared/services/user-details/user-details.service';
import { usePdfExportMode } from '../hooks/use-pdf-export-mode';

export const IntercomInitializer = () => {
  const { userDetails } = useContext(UserDetailsContext);
  const bootedAsRef = useRef<string | undefined>(undefined);
  const userService = new UserDetailsService();
  const pdfExportMode = usePdfExportMode();

  useEffect(() => {
    const appId = process.env.REACT_APP_INTERCOM_APP_ID;
    // A PDF render is a throwaway headless session: no chat launcher in the file.
    if (!appId || typeof window === 'undefined' || pdfExportMode) return;

    const w = window as any;

    if (typeof w.Intercom !== 'function') {
      const i: any = function () { i.c(arguments as any); } as any;
      i.q = [] as any[];
      i.c = function (args: any) { i.q.push(args); };
      w.Intercom = i;
      const d = document; const s = d.createElement('script');
      s.type = 'text/javascript'; s.async = true; s.src = `https://widget.intercom.io/widget/${appId}`;
      const x = d.getElementsByTagName('script')[0]; x?.parentNode?.insertBefore(s, x);
    }

    const isLoggedIn = Boolean(userDetails?.id);
    const key = isLoggedIn ? (userDetails?.id as string) : 'anonymous';

    if (bootedAsRef.current === key) {
      if (isLoggedIn) {
        w.Intercom('update', {
          name: [userDetails?.firstName, userDetails?.surname].filter(Boolean).join(' '),
          email: userDetails?.email,
        });
      }
      return;
    }

    if (bootedAsRef.current) {
      w.Intercom('shutdown');
    }

    if (isLoggedIn) {
      const bootWithToken = (token?: string) => {
        w.Intercom('boot', {
          app_id: appId,
          api_base: 'https://api-iam.intercom.io',
          user_id: userDetails?.id,
          name: [userDetails?.firstName, userDetails?.surname].filter(Boolean).join(' '),
          email: userDetails?.email,
          intercom_user_jwt: token,
          hide_default_launcher: false,
        });
      };
      userService.getIntercomJwt().then(t => bootWithToken(t)).catch(() => bootWithToken());
    } else {
      w.Intercom('boot', { app_id: appId, api_base: 'https://api-iam.intercom.io', hide_default_launcher: false });
    }
    bootedAsRef.current = key;
  }, [userDetails, pdfExportMode]);

  return null;
};

export default IntercomInitializer;
